import { useEffect, useState } from 'react'
import {
  CalendarRange, Camera, Coins, Handshake, Music4, Search, Sparkles, TrendingUp, Radar, Ticket,
} from 'lucide-react'

import { Shell, useShellMeta } from '../admin/Shell.js'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { apiGet, usePermissions } from '../admin/primitives.js'
import { navigate } from '../router.js'

/**
 * RÉGLAGES SOIRÉES — UN HUB, PLUS UN INTERMINABLE DÉROULÉ.
 *
 * Tout tenait sur une page unique : saisons, familles, salles, veille,
 * Instagram et prévisions se succédaient sans hiérarchie, et trouver un
 * réglage supposait de savoir à quelle hauteur il se trouvait.
 *
 * Chaque domaine a désormais sa page. Les vignettes annoncent l'ÉTAT réel de
 * chacun — un canal éteint ou une connexion expirée se voit d'ici, sans avoir
 * à ouvrir la page pour le découvrir.
 */

interface Etat {
  market?: { enabled?: boolean }
  forecast?: { enabled?: boolean }
  families?: string[]
  websearch?: { enabled?: boolean; base_url?: string | null }
  mc_rooms?: Record<string, string>
  detected_seasons?: { name: string }[]
  night_cash?: { soiree_page?: boolean; week_cards?: boolean; history?: boolean; calendar?: boolean; closed_revenue?: boolean }
  billetteries?: { live_count?: 'NIGHT' | 'SOIREE'; include_in_analytics?: boolean; attach_mode?: 'CERTAIN_AND_PROBABLE' | 'CERTAIN_ONLY' | 'NEVER' }
}

interface JetonMeta { etat: string; jours_restants: number | null }

/**
 * « Affichée partout, CA arrêté ou validé compris » — ou ce qui est éteint,
 * en clair. « ou validé » (19/10/2026) : le même interrupteur masque le CA
 * validé des nuits reprises ; ne dire que « arrêté » le rendait introuvable.
 */
function resumeCaisse(c: Etat['night_cash']): string {
  if (!c) return 'Rapports et compté de la nuit, en petit dans les Soirées'
  const lieux = [c.soiree_page, c.week_cards, c.history, c.calendar].filter((x) => x !== false).length
  if (lieux === 0) return 'Masquée partout'
  const ca = c.closed_revenue === false ? ', sans le CA arrêté ni validé' : ', CA arrêté ou validé compris'
  return lieux === 4 ? `Affichée partout${ca}` : `Affichée à ${lieux} endroits sur 4${ca}`
}

export function SoireesSettingsHub() {
  const meta = useShellMeta()
  const [s, setS] = useState<Etat | null>(null)
  const [jeton, setJeton] = useState<JetonMeta | null>(null)
  // Associations (20/10/2026) : leurs droits DÉDIÉS — la carte n'apparaît qu'à qui peut ouvrir la page.
  const permissions = usePermissions()
  const litAssos = permissions.permissions.includes('association.read')
  // Billetterie Live Club (22/10/2026) : ses réglages vivent dans Réglages › Billetterie ; on y renvoie.
  const litBilletterieMaison = permissions.permissions.includes('ticketing.event.read')
  const [assos, setAssos] = useState<{ recognition_mode?: string } | null>(null)
  useEffect(() => {
    if (!litAssos) return
    void apiGet<{ settings?: { recognition_mode?: string } }>('/api/v1/settings/associations')
      .then((j) => setAssos(j.settings ?? null)).catch(() => setAssos(null))
  }, [litAssos])

  useEffect(() => {
    void apiGet<Etat>('/api/v1/soirees/settings').then(setS).catch(() => setS(null))
    void apiGet<JetonMeta>('/api/v1/market/social/instagram/token')
      .then(setJeton).catch(() => setJeton(null))
  }, [])

  /* L'état Instagram est le seul qui puisse être ALARMANT depuis ce hub : une
     connexion expirée arrête la collecte sans rien casser d'autre, et c'est
     précisément le genre de panne qui passe inaperçue des semaines. */
  const instagram = jeton === null ? '…'
    : jeton.etat === 'EXPIRE' ? 'Connexion expirée — plus rien n’est relevé'
    : jeton.etat === 'REVOQUE' ? 'Accès retiré côté Meta — plus rien n’est relevé'
    : jeton.etat === 'BIENTOT_EXPIRE'
      ? `Connexion valide — expire dans ${jeton.jours_restants} jours`
    : jeton.etat === 'ABSENT' ? 'Non connecté'
    : jeton.etat === 'INDETERMINE' ? 'État inconnu — Meta n’a pas répondu'
    : 'Connexion active'

  const alerteInstagram = jeton !== null
    && (jeton.etat === 'EXPIRE' || jeton.etat === 'REVOQUE' || jeton.etat === 'BIENTOT_EXPIRE')

  const cartes = [
    {
      href: '/settings/soirees/saisons', titre: 'Saisons', Icone: CalendarRange, tint: 'violet',
      meta: s?.detected_seasons?.length
        ? `${s.detected_seasons.length} saison${s.detected_seasons.length > 1 ? 's' : ''} détectée${s.detected_seasons.length > 1 ? 's' : ''} sur l’historique`
        : 'Bornes déduites de l’historique réel',
    },
    {
      href: '/settings/soirees/familles', titre: 'Familles d’événements', Icone: Sparkles, tint: 'violet',
      meta: s?.families?.length
        ? `${s.families.length} famille${s.families.length > 1 ? 's' : ''} confirmée${s.families.length > 1 ? 's' : ''}`
        : 'Aucune famille confirmée',
    },
    {
      href: '/settings/soirees/salles', titre: 'Salles & DJs', Icone: Music4, tint: 'cyan',
      meta: 'Référentiel des salles et annuaire des DJs',
    },
    {
      href: '/settings/soirees/veille', titre: 'Veille concurrentielle', Icone: Radar, tint: 'amber',
      meta: s?.market?.enabled === false
        ? 'Éteinte — aucun établissement n’est relevé'
        : 'Établissements surveillés, sources et cadence',
    },
    {
      href: '/settings/soirees/instagram', titre: 'Instagram', Icone: Camera,
      tint: alerteInstagram ? 'amber' : 'cyan', meta: instagram,
    },
    {
      href: '/settings/soirees/recherche-web', titre: 'Recherche web', Icone: Search, tint: 'neutral',
      meta: s?.websearch?.enabled
        ? 'Instance auto-hébergée active — aucun coût'
        : 'Éteinte — aucune instance renseignée',
    },
    {
      href: '/settings/soirees/previsions', titre: 'Prévisions', Icone: TrendingUp, tint: 'emerald',
      meta: s?.forecast?.enabled === false
        ? 'Éteintes — aucune projection n’est produite'
        : 'Méthode déterministe, archivée et confrontée au réel',
    },
    {
      href: '/settings/soirees/caisse', titre: 'Caisse de la nuit', Icone: Coins, tint: 'neutral',
      meta: resumeCaisse(s?.night_cash),
    },
    {
      href: '/settings/soirees/billetteries', titre: 'Billetteries', Icone: Ticket, tint: 'cyan',
      meta: s?.billetteries
        ? `${s.billetteries.live_count === 'SOIREE' ? 'La soirée seule' : 'Le total de la nuit'} dans le direct${s.billetteries.include_in_analytics ? ' · associations dans les analyses' : ''}`
          + (s.billetteries.attach_mode === 'NEVER' ? ' · rattachement : toujours demandé'
            : s.billetteries.attach_mode === 'CERTAIN_ONLY' ? ' · rattachement seul si certain' : ' · rattachement seul si certain ou probable')
        : 'Shotgun et Pretix dans le direct, rattachement à la soirée du club',
    },
    ...(litAssos ? [{
      href: '/settings/soirees/associations', titre: 'Associations', Icone: Handshake, tint: 'violet',
      meta: assos?.recognition_mode === 'NEVER' ? 'Reconnaissance automatique éteinte : tout passe par « À décider »'
        : assos?.recognition_mode === 'CERTAIN_ONLY' ? 'Reconnues seules quand c’est certain · alias, mots exclus, tendance'
          : 'Reconnues seules quand c’est certain ou probable · alias, mots exclus, tendance',
    } as const] : []),
    ...(litBilletterieMaison ? [{
      href: '/settings/billetterie', titre: 'Billetterie Live Club', Icone: Ticket, tint: 'cyan',
      meta: 'Gel des modifications depuis Gestion, modèles de tarifs, réglages de la billetterie : dans Réglages › Billetterie',
    } as const] : []),
  ] as const

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger">
        <SettingsCrumb />
        <h1 className="lc-title"><Sparkles size={26} aria-hidden="true" /> Soirées</h1>
        <p className="lc-subtitle">
          Saisons, familles, salles, veille concurrentielle, prévisions, caisse de la nuit, billetteries et associations.
        </p>

        <div className="ad-settings-grid">
          {cartes.map((c) => (
            <button
              key={c.href}
              className={`lc-card ad-set-card ${c.tint !== 'neutral' ? `lc-card--${c.tint}` : 'gd-card--muted'}`}
              onClick={() => navigate(c.href)}
            >
              <div className="ad-set-card__head">
                <span
                  className={`lc-card__icon ${c.tint === 'neutral' ? 'gd-icon--muted' : ''}`}
                  style={{ width: 46, height: 46, borderRadius: 13 }} aria-hidden="true"
                >
                  <c.Icone size={22} />
                </span>
                <span className="ad-set-card__title">{c.titre}</span>
              </div>
              <span className="ad-set-card__meta">{c.meta}</span>
            </button>
          ))}
        </div>
      </main>
    </Shell>
  )
}
